import { InfoCircleOutlined } from '@ant-design/icons';
import { t } from '@framework/i18n';
import { ContentLayoutWrapper } from '@framework/ui';
import { Button } from 'antd';
import styled from 'styled-components';
import { getLegalEntityTypeLabel } from './legalEntityDisplay';
import { useLegalEntityIdentityActions } from './useLegalEntityIdentityActions';
import type { SelectedLegalEntity } from './useSelectedLegalEntity';
import { useSelectedLegalEntity } from './useSelectedLegalEntity';

const BannerWrapper = styled.div`
  min-height: 30px;
  background-color: ${props => props.theme.colorInfoBg};
  border-bottom: 1px solid ${props => props.theme.colorInfoBorder};
  padding: 12px 0;
  z-index: 10;
`;

const BannerLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
`;

const InfoSection = styled.div`
  grid-column: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const InfoIcon = styled(InfoCircleOutlined)`
  flex-shrink: 0;
  color: ${props => props.theme.colorInfo};
  font-size: 20px;
`;

const BannerText = styled.span`
  font-size: 0.875rem;
  line-height: 20px;
  color: ${props => props.theme.colorTextBase};
`;

const BoldName = styled.span`
  font-weight: 700;
`;

const ChangeProfileButton = styled(Button)`
  &&& {
    grid-column: 3;
    justify-self: end;
    flex-shrink: 0;
    height: auto;
    padding: 0;
    font-size: 0.875rem;
    line-height: 20px;
    white-space: nowrap;
  }
`;

/**
 * Contenuto del banner, separato per chiarezza.
 */
function BannerContent({
  selectedLegalEntity,
}: {
  selectedLegalEntity: SelectedLegalEntity;
}) {
  const { returnToSelection } = useLegalEntityIdentityActions();

  const typeLabel = getLegalEntityTypeLabel(selectedLegalEntity.tipoPg);
  const legalEntityLabel = typeLabel
    ? t(
        'frontoffice.legal-entity-banner.pg-type',
        'Persona Giuridica {{type}}',
        {
          type: typeLabel,
        },
      )
    : t('frontoffice.legal-entity-banner.pg', 'Persona Giuridica');

  return (
    <BannerWrapper>
      <ContentLayoutWrapper boxed>
        <BannerLayout>
          <InfoSection>
            <InfoIcon aria-hidden />
            <BannerText>
              {t(
                'frontoffice.legal-entity-banner.operating-as',
                'Stai operando per conto di',
              )}{' '}
              <BoldName>{selectedLegalEntity.ragioneSociale}</BoldName> (
              {legalEntityLabel})
            </BannerText>
          </InfoSection>
          <ChangeProfileButton
            type="link"
            onClick={() => {
              void returnToSelection();
            }}
          >
            {t(
              'frontoffice.legal-entity-banner.change-profile',
              'Cambia profilo',
            )}
          </ChangeProfileButton>
        </BannerLayout>
      </ContentLayoutWrapper>
    </BannerWrapper>
  );
}

/**
 * Banner informativo che indica la PG per conto della quale l'utente sta operando.
 */
export function FrontLegalEntityBanner() {
  const { selectedLegalEntity } = useSelectedLegalEntity();
  if (!selectedLegalEntity) return null;
  return <BannerContent selectedLegalEntity={selectedLegalEntity} />;
}
